/**
 * Rappels de planning, branchés globalement (App.vue) pour tout utilisateur
 * ayant un planning assigné :
 *
 *  1. **5 minutes avant chaque bloc** (réunion, pausette, déjeuner, reprise,
 *     formation, coaching…) : toast + notification native — « Dans 5 min :
 *     Pausette (15 min) ». Programmé LOCALEMENT depuis le planning (aucun
 *     serveur de push nécessaire).
 *  2. **Changement de planning** : le serveur expose une `version`
 *     (assignation | contenu). Un polling léger la compare à la dernière vue
 *     (localStorage) → « Votre planning a été mis à jour » à l'assignation ou
 *     à la modification par un gestionnaire.
 *
 * La fin de journée du jour respecte `weekday_end_overrides` (ex. vendredi
 * 21h00) : le dernier bloc est tronqué et aucun rappel n'est émis après.
 */
import { onMounted, onUnmounted, ref } from 'vue'
import { PlanningAPI, type Schedule } from '@/shared/api/planning'
import { useNotifications } from '@/shared/composables/useNotifications'
import { useToasts } from '@/shared/stores/toasts'

const LEAD_MS = 5 * 60 * 1000          // préavis : 5 minutes
const POLL_MS = 60 * 1000              // détection de changement : 1 min
const VERSION_KEY = 'pexitalk_schedule_version'

export function useScheduleNotifications() {
  const toasts = useToasts()
  const schedule = ref<Schedule | null>(null)

  let pollTimer: ReturnType<typeof setInterval> | null = null
  let reminderTimer: ReturnType<typeof setTimeout> | null = null
  const notifiedKeys = new Set<string>()

  /** Bornes du jour : blocs triés, dernier bloc tronqué selon le jour. */
  function todaysBlocks(s: Schedule): Array<{ at: Date; label: string }> {
    const now = new Date()
    const weekday = (now.getDay() + 6) % 7 // JS dim=0 → Python lun=0
    const override = s.weekday_end_overrides?.[String(weekday)]
    const capMin = override ? toMinutes(override) : null

    const events: Array<{ at: Date; label: string }> = []
    for (const b of s.blocks) {
      const startMin = toMinutes(b.start)
      if (capMin !== null && startMin >= capMin) continue // bloc annulé ce jour
      const at = new Date(now)
      at.setHours(Math.floor(startMin / 60), startMin % 60, 0, 0)
      events.push({ at, label: b.label || b.kind_label || b.kind })
    }
    // Fin de journée (dernier bloc, éventuellement plafonnée).
    if (s.blocks.length) {
      const lastEnd = Math.max(...s.blocks.map((b) => toMinutes(b.end)))
      const endMin = capMin !== null ? Math.min(lastEnd, capMin) : lastEnd
      const at = new Date(now)
      at.setHours(Math.floor(endMin / 60), endMin % 60, 0, 0)
      events.push({ at, label: 'Fin de journée' })
    }
    return events.sort((a, b) => a.at.getTime() - b.at.getTime())
  }

  function toMinutes(hm: string): number {
    const [h, m] = hm.split(':')
    return Number(h) * 60 + Number(m)
  }

  /** Programme le PROCHAIN rappel (un seul timer à la fois — reprogrammé
   *  après chaque tir ou rechargement du planning). */
  function scheduleNextReminder() {
    if (reminderTimer) { clearTimeout(reminderTimer); reminderTimer = null }
    const s = schedule.value
    if (!s) return
    const now = Date.now()
    for (const ev of todaysBlocks(s)) {
      const fireAt = ev.at.getTime() - LEAD_MS
      const key = `${ev.at.toDateString()}|${ev.at.getHours()}:${ev.at.getMinutes()}|${ev.label}`
      if (fireAt <= now || notifiedKeys.has(key)) continue
      reminderTimer = setTimeout(() => {
        notifiedKeys.add(key)
        const time = ev.at.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
        toasts.warning(`Dans 5 min : ${ev.label}`, `Prévu à ${time} sur votre planning.`)
        useNotifications().show('Planning — dans 5 min', `${ev.label} à ${time}`)
        scheduleNextReminder() // enchaîne sur le suivant
      }, fireAt - now)
      return
    }
  }

  async function refresh(notifyOnChange: boolean) {
    try {
      const res = await PlanningAPI.my()
      const last = localStorage.getItem(VERSION_KEY) || ''
      const current = res.version || ''
      if (notifyOnChange && current && current !== last) {
        // Première vue (last vide) = découverte, on notifie aussi : c'est le
        // cas « un nouveau planning vient de m'être assigné ».
        toasts.info('Planning mis à jour', 'Votre planning a été assigné ou modifié — consultez « Mon planning ».')
        useNotifications().show('Planning mis à jour', 'Consultez la page Mon planning.')
      }
      if (current !== last) localStorage.setItem(VERSION_KEY, current)
      schedule.value = res.schedule
      scheduleNextReminder()
    } catch { /* hors-ligne / non connecté : silencieux */ }
  }

  onMounted(() => {
    // Premier chargement SANS notification (l'utilisateur vient d'ouvrir
    // l'app) — sauf si la version a changé depuis sa dernière session.
    refresh(localStorage.getItem(VERSION_KEY) !== null)
    pollTimer = setInterval(() => refresh(true), POLL_MS)
  })
  onUnmounted(() => {
    if (pollTimer) clearInterval(pollTimer)
    if (reminderTimer) clearTimeout(reminderTimer)
  })

  return { schedule }
}
